import { useCallback, useEffect, useRef, useState } from "react";

type PageSize = { width: number; height: number };

/**
 * Tracks which PDF pages are near the visible viewport of a scroll
 * container, so callers can mount react-pdf's <Page> only for those and
 * render a same-size placeholder for the rest.
 */
export function usePdfPageWindow(
  containerRef: React.RefObject<HTMLElement>,
  numPages: number,
  resetKey: unknown
) {
  const [visiblePages, setVisiblePages] = useState<Set<number>>(
    () => new Set([1])
  );
  const [pageSize, setPageSize] = useState<PageSize | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pageElementsRef = useRef<Map<number, HTMLElement>>(new Map());

  useEffect(() => {
    setVisiblePages(new Set([1]));
    setPageSize(null);
  }, [resetKey]);

  useEffect(() => {
    if (!containerRef.current || numPages === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        setVisiblePages((prev) => {
          const next = new Set(prev);
          entries.forEach((entry) => {
            const pageNumber = Number(
              (entry.target as HTMLElement).dataset.pageNumber
            );
            if (entry.isIntersecting) next.add(pageNumber);
            else next.delete(pageNumber);
          });
          return next;
        });
      },
      { root: containerRef.current, rootMargin: "600px 0px" }
    );
    observerRef.current = observer;
    pageElementsRef.current.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [containerRef, numPages, resetKey]);

  const registerPageRef = useCallback(
    (pageNumber: number) => (el: HTMLElement | null) => {
      const previous = pageElementsRef.current.get(pageNumber);
      if (previous && observerRef.current)
        observerRef.current.unobserve(previous);
      if (el) {
        pageElementsRef.current.set(pageNumber, el);
        observerRef.current?.observe(el);
      } else {
        pageElementsRef.current.delete(pageNumber);
      }
    },
    []
  );

  const reportPageSize = useCallback((size: PageSize) => {
    setPageSize((prev) =>
      prev && prev.width === size.width && prev.height === size.height
        ? prev
        : size
    );
  }, []);

  const isPageVisible = useCallback(
    (pageNumber: number) => visiblePages.has(pageNumber),
    [visiblePages]
  );

  return { isPageVisible, registerPageRef, pageSize, reportPageSize };
}
